import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../components/Common';
import { CheckCircle, BarChart2, Shield, Zap, ArrowRight } from 'lucide-react';

const DemoPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();

    // State for Single Demo View
    const [config, setConfig] = useState(null);
    const [loadingConfig, setLoadingConfig] = useState(false);

    // State for Demo List (No Slug)
    const [demoList, setDemoList] = useState([]);

    // Form State
    const [formData, setFormData] = useState({
        name: '', company: '', location: '', state: '', country: '', email: '', contact_number: '', details: ''
    });
    const [submitLoading, setSubmitLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        const rawUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api/v1";
        const API_BASE = (window.location.protocol === 'https:' && rawUrl.startsWith('http:'))
            ? rawUrl.replace('http:', 'https:')
            : rawUrl;

        const fetchData = async () => {
            setLoadingConfig(true);
            try {
                if (slug) {
                    // Fetch Specific Demo Config
                    const res = await fetch(`${API_BASE}/tenant-onboarding/public-demo/${slug}`);
                    if (!res.ok) throw new Error("Demo not found");
                    const data = await res.json();
                    setConfig(data);
                } else {
                    // Fetch List of Active Demos
                    const res = await fetch(`${API_BASE}/tenant-onboarding/public-demos`);
                    if (res.ok) {
                        const data = await res.json();
                        setDemoList(data); // Expects [{ label, value, ui_config }]
                    }
                }
            } catch (err) {
                console.error("Failed to load demo data", err);
                setMessage("Demo not available.");
            } finally {
                setLoadingConfig(false);
            }
        };

        fetchData();
    }, [slug]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);
        setMessage('');

        try {
            const rawUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api/v1";
            const API_BASE = (window.location.protocol === 'https:' && rawUrl.startsWith('http:'))
                ? rawUrl.replace('http:', 'https:')
                : rawUrl;
            const response = await fetch(`${API_BASE}/tenant-onboarding/request-demo`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...formData,
                    slug: slug // Pass the slug from URL
                })
            });

            if (!response.ok) throw new Error("Request failed. Please try again.");
            setIsSuccess(true);
        } catch (error) {
            console.error("Demo request error:", error);
            setMessage("Failed to submit request: " + error.message);
        } finally {
            setSubmitLoading(false);
        }
    };

    if (loadingConfig) return <div className="h-screen flex items-center justify-center"><LoadingSpinner /></div>;

    if (isSuccess) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 text-center animate-fade-in-up">
                    <div className="flex justify-center mb-6">
                        <img src="/aidas_logo.png" alt="AIDAS Logo" className="h-12 w-auto" />
                    </div>
                    <div className="mx-auto w-20 h-20 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mb-6">
                        <CheckCircle className="w-10 h-10" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Request Sent!</h2>
                    {slug ? (
                        <>
                            <p className="text-gray-600 dark:text-gray-300 mb-8 text-lg">
                                You will receive an access link at <strong>{formData.email}</strong> shortly.
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="text-gray-600 dark:text-gray-300 mb-6 text-lg">
                                Thanks for your message, our team will reach out to you shortly.
                            </p>
                            <p className="text-gray-500 dark:text-gray-400 mb-8">
                                Visit <a href="https://www.aidastech.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.aidastech.com</a> to know more about us.
                            </p>
                        </>
                    )}
                    <a href="/" className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-md transition-colors">
                        Back to Home
                    </a>
                </div>
            </div>
        );
    }

    // --- CASE 1: NO SLUG (SHOW DIRECTORY) ---
    // --- CASE 1: NO SLUG (SHOW DIRECTORY OR LEAD CAPTURE) ---
    if (!slug) {
        return (
            <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center p-4">

                {demoList.length > 0 ? (
                    // SHOW LIST IF DEMOS EXIST
                    <div className="max-w-6xl w-full">
                        <div className="text-center mb-12">
                            <img src="/aidas_logo.png" alt="AIDAS Logo" className="h-16 mx-auto mb-6 opacity-90" />
                            <h1 className="text-4xl lg:text-5xl font-bold mb-6">Choose Your Demo Experience</h1>
                            <p className="text-xl text-gray-400">Select a prepared environment to explore the platform.</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {demoList.map(demo => (
                                <div key={demo.value} onClick={() => navigate(`/demo/${demo.value}`)}
                                    className="group bg-white/5 hover:bg-white/10 border border-white/10 p-6 rounded-2xl cursor-pointer transition-all hover:scale-[1.02]">
                                    <div className="mb-4">
                                        <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4 text-blue-400 group-hover:text-blue-300">
                                            <Zap className="w-6 h-6" />
                                        </div>
                                        <h3 className="text-xl font-bold mb-2">{demo.label}</h3>
                                        <p className="text-sm text-gray-400 line-clamp-2">
                                            {demo.ui_config?.description || "Explore this dedicated environment."}
                                        </p>
                                    </div>
                                    <div className="flex items-center text-blue-400 font-medium text-sm group-hover:gap-2 transition-all">
                                        Launch Demo <ArrowRight className="w-4 h-4 ml-1" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    // SHOW LEAD CAPTURE IF NO DEMOS
                    <div className="max-w-4xl w-full flex flex-col md:flex-row bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up">
                        {/* Left: Message */}
                        <div className="md:w-1/2 p-10 pt-24 bg-gradient-to-br from-blue-900 to-slate-900 text-white flex flex-col justify-start items-center text-center">
                            <img src="/aidas_logo.png" alt="AIDAS Logo" className="h-24 w-auto mb-8 filter brightness-0 invert" />
                            <h2 className="text-3xl font-bold mb-6">Thanks for your interest</h2>
                            <p className="text-lg text-blue-200 mb-6 leading-relaxed">
                                At the moment, there are no active public demo environments available.
                            </p>
                            <p className="text-gray-300">
                                Our business team is ready to set up a personalized trial tailored to your specific needs. Fill out the form, and we will reach out shortly.
                            </p>
                        </div>

                        {/* Right: Form */}
                        <div className="md:w-1/2 p-8 bg-gray-50 dark:bg-gray-800">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Request a Discovery Call</h3>
                            <form onSubmit={(e) => {
                                // Force slug to be general-inquiry for this specific form
                                // We hook into the existing submit logic but override the slug param
                                e.preventDefault();
                                // Manually calling the same logic but with hardcoded slug logic in body
                                setSubmitLoading(true);
                                setMessage('');

                                const rawUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api/v1";
                                const API_BASE = (window.location.protocol === 'https:' && rawUrl.startsWith('http:'))
                                    ? rawUrl.replace('http:', 'https:')
                                    : rawUrl;
                                fetch(`${API_BASE}/tenant-onboarding/request-demo`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                        ...formData,
                                        slug: 'general-inquiry'
                                    })
                                })
                                    .then(res => {
                                        if (!res.ok) throw new Error("Request failed");
                                        setIsSuccess(true);
                                    })
                                    .catch(err => setMessage("Failed to submit. Please try again."))
                                    .finally(() => setSubmitLoading(false));

                            }} className="space-y-4">

                                <input name="name" required value={formData.name} onChange={handleChange} placeholder="Full Name" className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" />
                                <input name="email" type="email" required value={formData.email} onChange={handleChange} placeholder="Work Email" className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" />
                                <input name="company" required value={formData.company} onChange={handleChange} placeholder="Company Name" className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" />

                                <input name="contact_number" required value={formData.contact_number} onChange={handleChange} placeholder="Contact Number" className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" />
                                <textarea name="details" required value={formData.details} onChange={handleChange} placeholder="Details of your interests/needs" rows={3} className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" />

                                <div className="grid grid-cols-2 gap-4">
                                    <input name="location" required value={formData.location} onChange={handleChange} placeholder="City" className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none" />
                                    <input name="state" required value={formData.state} onChange={handleChange} placeholder="State" className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none" />
                                    <div className="col-span-2">
                                        <input name="country" required value={formData.country} onChange={handleChange} placeholder="Country" className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none" />
                                    </div>
                                </div>

                                <button type="submit" disabled={submitLoading} className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors flex justify-center">
                                    {submitLoading ? <LoadingSpinner size={20} color="white" /> : 'Contact Business Team'}
                                </button>
                                {message && <p className="text-red-500 text-sm text-center">{message}</p>}
                            </form>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // --- CASE 2: INVALID SLUG / ERROR ---
    if (!config && !loadingConfig) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gray-50 dark:bg-gray-900 text-center">
                <img src="/aidas_logo.png" alt="AIDAS Logo" className="h-20 w-auto mb-8 opacity-80" />
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">Demo Not Active</h2>
                <p className="text-gray-500 mb-6">The requested demo environment "{slug}" is not active at the moment.</p>
                <a href="/demo" className="text-blue-600 hover:underline">View Available Demos</a>
            </div>
        );
    }

    // --- CASE 3: DEMO REGISTRATION FORM ---
    return (
        <div className="min-h-screen bg-gray-50/50 dark:bg-gray-900 flex flex-col lg:flex-row">
            {/* Left Column - Dynamic Content from Config */}
            <div className="hidden lg:flex w-full lg:w-1/2 bg-slate-900 relative overflow-hidden flex-col justify-between p-12 text-white">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-900 to-slate-900 opacity-90 z-0" />

                {/* Decorative blobs */}
                <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20" />
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20" />

                <div className="relative z-10 animate-fade-in">
                    <div className="flex items-center gap-3 mb-10">
                        <img src="/aidas_logo.png" alt="AIDAS Platform" className="h-24 w-auto filter brightness-0 invert opacity-90" />
                    </div>

                    <h1 className="text-5xl font-bold leading-tight mb-6">
                        {config.ui_config?.title || `Welcome to the ${config.name}`}
                    </h1>
                    <p className="text-xl text-gray-300 mb-10 max-w-lg">
                        {config.ui_config?.description || "Sign up below to get instant access to this demo environment."}
                    </p>

                    <div className="space-y-6">
                        <div className="flex items-start gap-4">
                            <div className="bg-white/10 p-3 rounded-lg backdrop-blur-sm"><BarChart2 className="w-6 h-6 text-blue-300" /></div>
                            <div>
                                <h3 className="font-semibold text-lg">Interactive Dashboard</h3>
                                <p className="text-gray-400 text-sm">Full access to explore data visualizations.</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="bg-white/10 p-3 rounded-lg backdrop-blur-sm"><Zap className="w-6 h-6 text-blue-300" /></div>
                            <div>
                                <h3 className="font-semibold text-lg">Instant Access</h3>
                                <p className="text-gray-400 text-sm">Passwordless magic link sent to your email.</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="relative z-10 text-sm text-gray-500 mt-12">
                    Protected by Enterprise Security
                </div>
            </div>

            {/* Right Column - Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-4 lg:p-12 overflow-y-auto">
                <div className="max-w-md w-full bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 animate-slide-up">
                    <div className="mb-8">
                        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Get Started</h2>
                        <p className="text-gray-500 dark:text-gray-400">
                            Enter your details to access the <strong>{config.slug}</strong> environment.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Full Name</label>
                                <input name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 dark:text-white outline-none" placeholder="Jane Doe" />
                            </div>
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Work Email</label>
                                <input name="email" type="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 dark:text-white outline-none" placeholder="jane@company.com" />
                            </div>
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Company</label>
                                <input name="company" required value={formData.company} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 dark:text-white outline-none" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">City</label>
                                <input name="location" required value={formData.location} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 dark:text-white outline-none" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">State</label>
                                <input name="state" required value={formData.state} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 dark:text-white outline-none" />
                            </div>
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Country</label>
                                <input name="country" required value={formData.country} onChange={handleChange} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 dark:text-white outline-none" />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={submitLoading}
                            className="w-full py-3.5 mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-500/30 flex justify-center items-center transition-all active:scale-[0.98]"
                        >
                            {submitLoading ? <LoadingSpinner size={20} color="white" /> : 'Start Free Demo'}
                        </button>

                        {message && <p className="text-center text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">{message}</p>}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default DemoPage;
